import validator from 'validator';
import {putAll, putAllUnique} from 'utils/Array';
import {replacePlaceholders} from 'common/Replace'

/**
 * The engine implements validation group based approach.
 * Object which describe the business model validation rules could contain one or more validation groups.
 *
 * Validation group is a set of validation rules, which have the same:
 *   1) time of processing (e.g. the same button click),
 *   2) place of demonstration (e.g. the same errorBox).
 * Validation rules should be added to different groups, if one of the listed conditions is not satisfied.
 *
 * Validation rule describes:
 *   1) which data should be analyzed
 *   2) which individual elements (=individuals) should be invalidated if error is found (e.g. inputs, selects)
 *   3) which validation strategies should be applied.
 * Validation strategies should be defined in different rules, if they process different values or invalidate different individuals.
 *
 * Validation strategies could be imported from the package, but it is also possible to implement custom strategy.
 *
 * Example of prepared validation object:
 * const validation = {
 *   main: [                                                 // validation group "main" with one validation rule inside
 *     {
 *       properties: ["state.comment", "state.attachments"], // array of property paths to analyze
 *
 *       individualNames: ["comment", "attachments"],        // array of individual names to invalidate if error occurs
 *
 *       strategies: [                                       // array of strategies to apply (with one strategy)
 *
 *         atLeastOneNotEmpty("error message")               // the strategy is imported from validation package
 *                                                           //   and initialized with one parameter - error message to show if error occurs
 *       ]
 *     }
 *   ]
 * };
 *
 * The usage example could be the following:
 * const validationResult = validate(this, validation, ["main"]);  // process "main" validation group from provided validation object,
 *                                                                 //   use this object as properties source
 * if (validationResult.isEmpty()) {
 *   ...                                                           // business logic for valid state
 * } else {
 *   this.setState({
 *     errorMessages: validationResult.getErrorMessages()          // render found errors
 *   });
 * }
 *
 * To show desired errors you could use ErrorBox:
 * <ErrorBox groups={["main"]} errorMessages={this.state.errorMessages}/>   // only "main" group results will be shown
 *
 * To mark desired individuals you should assign the individual name:
 * <TextArea name="comment" errorMessages={this.state.errorMessages} .../>
 */

 /**
 * Extracts all errors which were added for individual with the given name.
 * Optionally table row index could be provided for {@link forEach} generated messages.
 *
 * @param errorMessages collected error messages object
 * @param individualName name of the individual to collect errors for
 * @param tableIndex (optional) index of the individual in the table
 * @returns {Array} error messages to show.
 */
export function extractIndividualErrors(errorMessages, individualName, tableIndex) {
    let result = [];
    if (errorMessages) {
        Object.keys(errorMessages).forEach(groupName => {
            let individualErrors = errorMessages[groupName];
            if (individualErrors) {
                let addedMessages;
                if (typeof tableIndex === "number" && tableIndex >= 0) {
                    const individualArray = individualErrors[individualName];
                    if (individualArray) {
                        addedMessages = individualArray[tableIndex];
                    }
                } else {
                    addedMessages = individualErrors[individualName];
                }
                result = putAllUnique(result, addedMessages);
            }
        });
    }
    return result;
}

/**
 * Extracts all errors which were added for validation groups the given names.
 * @param errorMessages collected error messages object
 * @param groups group names to collect errors for
 * @returns {Array} error messages to show.
 */
export function extractGroupErrors(errorMessages, groups) {
    let result = [];
    if (errorMessages && groups) {
        groups.forEach(groupName => {
            const groupObject = errorMessages[groupName];
            result = putAllUnique(result, collectMessages(groupObject));
        });
    }
    return result;
}

function collectMessages(containerObject) {
    let result = [];
    if (containerObject) {
        Object.keys(containerObject).forEach(fieldName => {
            const fieldMessages = containerObject[fieldName];
            if (fieldMessages instanceof Array) {
                result = putAll(result, fieldMessages);
            } else {
                result = putAll(result, collectMessages(fieldMessages));
            }
        });
    }
    return result;
}
/**
 * Name to use for the cases, when error should be shown, but no individuals should be invalidated.
 */
const DUMMY_INDIVIDUAL = "dummy";

/**
 * Class which contains collected error messages and provides methods to change them manually.
 */
export class ValidationResult {
    errorMessages;

    /**
     * Creates new instance.
     * @param errorMessages (optional) initial errorMessages.
     */
    constructor(errorMessages) {
        this.errorMessages = errorMessages || {};
    }

    /**
     * @returns collected error messages.
     */
    getErrorMessages() {
        return this.errorMessages;
    }

    /**
     * Returns all error messages collected for specific validation group.
     * @param groupName the desired validation group.
     * @returns See description.
     */
    getGroup(groupName) {
        return this.errorMessages[groupName] || {};
    }

    /**
     * Manually overrides all messages collected for the specified group and individual names with the given error.
     * @param groupName name of the group to override
     * @param error new error message
     * @param individualNames (optional) individual names to override. dummy name will be used if parameter in undefined
     */
    setError(groupName, error, individualNames) {
        const individualNamesToAdd = individualNames || [DUMMY_INDIVIDUAL];
        const groupToModify = this.errorMessages[groupName] || {};
        individualNamesToAdd.forEach(individualName => {
            groupToModify[individualName] = [error];
        });
        this.setGroup(groupName, groupToModify);
    }

    /**
     * Overrides the error group content with the given one.
     * @param groupName name of the group to override
     * @param value new group content
     */
    setGroup(groupName, value) {
        this.errorMessages[groupName] = value;
    }

    /**
     * Resets all error messages in the given groups.
     * @param groupNames names of groups to reset.
     */
    resetGroups(groupNames) {
        groupNames.forEach(groupName => this.setGroup(groupName, {}));
    }

    /**
     * Resets all error messages in the given group with the given index ({@link forEach} strategy collected only).
     * @param groupName name of the group to reset.
     * @param indexToReset index of the messages to reset.
     */
    resetByIndex(groupName, indexToReset) {
        const groupToUpdate = this.errorMessages[groupName] || {};
        Object.keys(groupToUpdate).forEach(individualName => {
            const individual = groupToUpdate[individualName];
            if (!(individual instanceof Array)) {
                const newIndividual = {};
                Object.keys(individual).forEach(index => {
                    if (parseInt(index, 10) !== indexToReset) {
                        newIndividual[indexToReset < index  ? index-1 : index] = individual[index];
                    }
                });
                groupToUpdate[individualName] = newIndividual;
            }
        });
    }

    /**
     * Checks if no error message was collected for the given groups.
     * @param groupNames (optional) names of groups to check. Check all groups if parameter is undefined.
     * @returns {boolean} see description.
     */
    isEmpty(groupNames) {
        const groupsToCheck = groupNames || Object.keys(this.errorMessages);
        return extractGroupErrors(this.errorMessages, groupsToCheck).length === 0;
    }
}

/**
 * Processes validation.
 * @param that object to use as properties source.
 * @param validation the validation rule description object.
 * @param groupsToValidate names of groups to process.
 * @returns {@link ValidationResult} object with collected error messages.
 */
export function validate(that, validation, groupsToValidate) {
    return processValidation(that, validation, groupsToValidate);
}

/**
 * Processes validation in the same way as {@link validate}, but with initial errors provided.
 *
 * @param that object to use as properties source.
 * @param validation the validation rule description object.
 * @param groupsToValidate names of groups to process.
 * @param previousMessages initial error messages.
 * @returns {@link ValidationResult} object with new collected error messages for groupsToValidate, but with previous for other groups.
 */
export function revalidate(that, validation, groupsToValidate, previousMessages) {
    const previousResult = new ValidationResult(previousMessages);
    previousResult.resetGroups(groupsToValidate);
    return processValidation(that, validation, groupsToValidate, previousResult);
}

function processValidation(that, validation, groupsToValidate, previousResult) {
    const validationResult = previousResult || new ValidationResult();
    Object.keys(validation).forEach(groupName => {
        if (groupsToValidate.includes(groupName)) {
            const ruleResults = processRules(that, validation[groupName]);
            ruleResults.forEach(ruleResult => processRuleResult(validationResult, groupName, ruleResult));
        }
    });
    return validationResult;
}

function processRuleResult(validationResult, groupName, ruleResult) {
    ruleResult.items.forEach(item => {
        if (item instanceof SwitchResult) {
            addSwitchError(validationResult, groupName, item);
        } else if (item instanceof ForEachResult) {
            addForEachError(validationResult, groupName, item);
        } else {
            addPlainError(validationResult, groupName, ruleResult.individualNames, item);
        }
    });
}

function addPlainError(validationResult, groupName, individualNames, error) {
    const individualNamesToAdd = individualNames || [DUMMY_INDIVIDUAL];
    const groupToModify = validationResult.getGroup(groupName);
    individualNamesToAdd.forEach(individualName => {
        const individualToModify = groupToModify[individualName] || [];
        groupToModify[individualName] = putAllUnique(individualToModify, error);
    });
    validationResult.setGroup(groupName, groupToModify);
}

function addSwitchError(validationResult, groupName, switchResult) {
    switchResult.errors.forEach(nestedRuleResult => processRuleResult(validationResult, groupName, nestedRuleResult));
}

function addForEachError(validationResult, groupName, forEachResult) {
    forEachResult.errors.forEach(indexRuleResult => {
        Object.keys(indexRuleResult).forEach(index => {
            const nestedRuleResult = indexRuleResult[index];
            addIndexError(validationResult, groupName, nestedRuleResult.individualNames, index, nestedRuleResult.items)
        });
    });
}

function addIndexError(validationResult, groupName, individualNames, index, items) {
    if (items.length !== 0) {
        const groupToModify = validationResult.getGroup(groupName);
        individualNames.forEach(individualName => {
            const individualToModify = groupToModify[individualName] || {};
            const indexToModify = individualToModify[index] || [];
            individualToModify[index] = putAllUnique(indexToModify, items);
            groupToModify[individualName] = individualToModify;
        });
        validationResult.setGroup(groupName, groupToModify);
    }
}

function processRules(checkedObject, rules) {
    let result = [];
    if (rules) {
        rules.forEach(rule => {
            result = putAll(result, processRule(checkedObject, rule));
        });
    }
    return result;
}

class RuleResult {
    items;
    individualNames;

    constructor(items, individualNames) {
        this.items = items || [];
        this.individualNames = individualNames;
    }
}

function processRule(checkedObject, rule) {
    let errors = [];
    const properties = evaluateProperties(checkedObject, rule.properties);
    rule.strategies.some(strategy => {
        let strategyResult = strategy(properties);
        errors = putAll(errors, strategyResult.errors);
        return strategyResult.stop;
    });
    return new RuleResult(errors, rule.individualNames);
}

/**
 * Sets focus on the first erroneous element.
 *
 * A description object should be provided as parameter.
 * The object maps individual name to element id OR function which provides HTMLElement.
 * Order of the pairs is used as priority - focus is set on the first element with found error.
 *
 * @param elementOrder the description object sorted
 * @param errorMessages collected error messages object
 */
export const focusFirstError = (elementOrder, errorMessages) => {
    Object.entries(elementOrder).some(([name, target]) => {
        const individualErrors = findFirstError(errorMessages, name);

        if (individualErrors) {
            let htmlElement;
            if (individualErrors instanceof Array) {
                htmlElement = findHtmlElement(target);
            } else {
                const firstIndex = Object.keys(individualErrors)[0];
                htmlElement = findHtmlElement(target, firstIndex);
            }
            htmlElement.focus();
            return true;
        }
    });
};

/**
 * Concatenates prefix with element index to obtain the specific element id.
 * @param prefix element prefix
 * @param index row index
 * @returns the specified html element.
 */
export const findTableElement = (prefix) => (index) => {
    return findHtmlElement(prefix + index);
};

const findFirstError = (errorMessages, individualName) => {
    let result = null;
    Object.values(errorMessages).some(groupErrors => {
        if (groupErrors) {
            const individualErrors = groupErrors[individualName];
            if (individualErrors) {
                result = individualErrors;
                return true;
            }
        }
    });
    return result;
};

const findHtmlElement = (target, index) => {
    if (target instanceof Function) {
        return target(index);
    } else {
        return document.getElementById(target);
    }
};

class ForEachResult {
    errors;

    constructor(errors) {
        this.errors = errors;
    }
}

/**
 * Specific validation strategy to collect specific individual errors for tables. All validation rules are applied for every table row.
 * Does not stop the strategies processing in one rule.
 * 
 * @param rules the validation rule array (with the same syntax as in validation group definition).
 */
export const forEach = (rules) => ([arrayToProcess]) => {
    let errors = [];
    rules.forEach(rule => {
        const indexRuleResult = {};
        arrayToProcess.forEach((element, index) => {
            indexRuleResult[index] = processRule(element, rule);
        });
        errors.push(indexRuleResult);
    });
    return {
        errors: new ForEachResult(errors),
        stop: false
    };
};

class SwitchResult {
    errors;

    constructor(errors) {
        this.errors = errors;
    }
}

/**
 * Specific validation strategy to apply rule sets based on specifies condition.
 * Every case contains value to analyze and validation rule array (with the same syntax as in validation group definition).
 * Case is processed if its value equals current switchValue.
 * 
 * Does not stop the strategies processing in one rule.
 *
 * @param switchValueName property name to decide if case should be applied.
 * @param cases object with switch cases definition.
 */
export const switchRules = (switchValueName, cases) => ([objectToProcess]) => {
    let errors = [];
    const switchValue = evaluateProperty(objectToProcess, switchValueName);
    Object.keys(cases).forEach(caseValue => {
        if (switchValue === caseValue) {
            const ruleResults = processRules(objectToProcess, cases[caseValue]);
            errors = putAll(errors, ruleResults);
        }
    });
    return {
        errors: new SwitchResult(errors),
        stop: false
    };
};

/**
 * Validation strategy for required business value. Valid string is not empty, valid array contains at least one element. * 
 * Do stop the strategies processing in one rule - if property is empty, it doesn't make sense to continue checking. 
 * 
 * @param errorMessage message to show if error is found.
 */
export const required = (errorMessage) => ([property]) => {
    let errors = [errorMessage];
    const isNotUndefined = property !== undefined;
    let isNotEmpty;
    if (isNotUndefined) {
        if (property instanceof Array) {
            isNotEmpty = property.length !== 0;
        } else if (typeof property === "string") {
            isNotEmpty = property.trim().length !== 0;
        } else if (typeof property === "boolean") {
            isNotEmpty = property;
        } else {
            isNotEmpty = true; // instanceof Object === true
        }
    }
    if (isNotUndefined && isNotEmpty) {
        errors = [];
    }
    return generateStrategyResult(errors, true);
};

/**
 * Validation strategy for business value which have prohibited value. Valid value is not equal to prohibited one.
 * Does not stop the strategies processing in one rule.
 *
 * @param prohibitedValue the invalid value.
 * @param errorMessage message to show if error is found.
 */
export const notEqual = (prohibitedValue, errorMessage) => ([property]) => {
    let errors = [];
    if (property === prohibitedValue) {
        errors = [errorMessage];
    }
    return generateStrategyResult(errors);
};

/**
 * Validation strategy for business value which has limited length. Valid value satisfies the length requirements.
 * Does not stop the strategies processing in one rule.
 *
 * @param minLength min value length.
 * @param maxLength max value length.
 * @param errorMessage message to show if error is found.
 */
export const limitedLength = (minLength, maxLength, errorMessage) => ([property]) => {
    let errors = [];
    if (!validator.isLength(property, {min: minLength, max: maxLength})) {
        errors = [errorMessage];
    }
    return generateStrategyResult(errors);
};

/**
 * Validation strategy for business value which has specific format. Valid value satisfies the format requirements.
 * Does not stop the strategies processing in one rule.
 *
 * @param pattern the format pattern.
 * @param errorMessage message to show if error is found.
 */
export const format = (pattern, errorMessage) => ([property]) => {
    let errors = [];
    if (!validator.matches(property, pattern)) {
        errors = [errorMessage];
    }
    return generateStrategyResult(errors);
};

/**
 * Validation strategy for business value which has email format. Valid value satisfies the email format requirements.
 * Does not stop the strategies processing in one rule.
 *
 * @param errorMessage message to show if error is found.
 */
export const emailFormat = (errorMessage) => ([property]) => {
    let errors = [];
    if (!validator.isEmail(property)) {
        errors = [errorMessage];
    }
    return generateStrategyResult(errors);
};

/**
 * Validation strategy for business values which cannot be empty at the same time. Values are valid if at least one of the is not empty.
 * Does not stop the strategies processing in one rule.
 *
 * @param errorMessage message to show if error is found.
 */
export const atLeastOneNotEmpty = (errorMessage) => (properties) => {
    let error = true;
    properties.some(property => {
        if (property.length !== 0) {
            error = false;
            return true;
        } else {
            return false;
        }
    });
    return generateStrategyResult(error ? [errorMessage] : []);
};

/**
 * Validation strategy for business values which should be equal. Values are valid if all of them are equal.
 * Does not stop the strategies processing in one rule.
 *
 * @param errorMessage message to show if error is found.
 */
export const allAreEqual = (errorMessage) => (properties) => {
    let first = properties[0];
    let error = !properties.every(element => element === first);
    return generateStrategyResult(error ? [errorMessage] : []);
};

/**
 * Validation strategy for business values which should not been contained in specific collection.
 * Value is valid if it's not contained in the given prohibited values.
 * Does not stop the strategies processing in one rule.
 *
 * @param fieldName field to check in prohibited objects
 * @param errorMessage message to show if error is found.
 */
export const isNotContainedIn = (fieldName, errorMessage) => ([value, prohibitedList]) => {
    let errors = [];
    if (prohibitedList.map(p => p[fieldName]).includes(value)) {
        errors = [errorMessage];
    }
    return generateStrategyResult(errors)
};

/**
 * Validation strategy for files which have limited size. The files are valid if all of them satisfy the size requirement.
 * Does not stop the strategies processing in one rule.
 *
 * @param maxFileSize max file size.
 * @param errorMessage message to show if error is found.
 */
export const limitedFileSize = (maxFileSize, errorMessage) => ([files]) => {
    let error = false;
    files.some(file => {
        if (file.size > maxFileSize) {
            error = true;
            return true;
        } else {
            return false;
        }
    });
    return generateStrategyResult(error ? [errorMessage] : []);
};

/**
 * Validation strategy for files which have limited type. The files are valid if all of them satisfy the type requirement.
 * Does not stop the strategies processing in one rule.
 *
 * @param allowedTypes the array of allowed file types.
 * @param errorMessageTemplate template to generate messages if errors are found. Invalid file name is added to result message.
 */
export const limitedFileType = (allowedTypes, errorMessageTemplate) => ([files]) => {
    let errors = [];
    files.forEach(file => {
        if(!allowedTypes.includes(file.type)) {
            errors.push(replacePlaceholders(errorMessageTemplate, file.name));
        }
    });
    return generateStrategyResult(errors);
};

/**
 * Generates internal strategy result to process. Could be used in custom strategies implementation.
 * @param errors array of the collected error messages.
 * @param stopIfError (optional) Should the processing be stopped if errors are not empty? false by default
 * @returns strategy result object.
 */
export function generateStrategyResult(errors, stopIfError = false) {
    return {
        errors: errors,
        stop: errors.length !== 0 ? stopIfError : false
    };
}

function evaluateProperties(that, propertyPaths) {
    let result = [];
    if (propertyPaths) {
        propertyPaths.forEach(propertyPath => {
            result.push(evaluateProperty(that, propertyPath));
        });
    }
    return result;
}

function evaluateProperty(that, propertyPath) {
    let result = that;
    if (propertyPath && propertyPath.length > 0) {
        propertyPath.split(".").forEach(property => result = result[property]);
    }
    return result;
}


// WEBPACK FOOTER //
// ./src/validation/index.js